import { isRecordingActive } from '../realtime/frames';
import type { RecordingState } from '../realtime/frames';

/**
 * Recording control REST surface (control/internal/httpapi/recording.go, docs/contract.md M6):
 * start/stop toggling, the room's recording history and file downloads, all authenticated with
 * the join JWT as a bearer token and all moderator/admin gated server-side (participants get
 * 403 forbidden). Same-origin /api calls, exactly like join/remint/history.
 */

/** Statuses of one recording (db 0008 CHECK, carried verbatim on the wire). */
export type RecordingStatus = RecordingState;

/** Statuses of one produced recording file (db 0008 CHECK; 0021/CR-30 adds no_key). */
export type RecordingFileStatus = 'pending' | 'ready' | 'missing' | 'processing' | 'no_key';

/** Purposes of one recording file (control + worker vocabulary; CR-30 adds summary). */
export type RecordingFilePurpose =
  | 'audio'
  | 'video'
  | 'mp3'
  | 'transcript'
  | 'merged'
  | 'overview'
  | 'summary';

/** One produced file of a recording (recordingFileJSON on the wire). */
export interface RecordingFile {
  readonly id: number;
  readonly purpose: RecordingFilePurpose;
  readonly status: RecordingFileStatus;
  readonly sizeBytes: number | null;
  readonly durationMs: number | null;
  /**
   * Short-lived signed playback URL of a READY media artifact (CR-29): the plain <video> src
   * the /recordings player streams through (a plain src cannot carry the Authorization
   * header). Absent when the server has no signing key or the artifact is not ready - the
   * player falls back to the bearer blob path.
   */
  readonly streamUrl?: string | undefined;
  /**
   * Participant channel identity the artifact belongs to (CR-35): `kc-<uuid>` for a room
   * publisher, `kc-<uuid>-screen` for its screen share. Carried by every per-channel row
   * (audio/video/mp3/transcript); composite artifacts (merged/overview/summary) have none.
   * The full transcript feed resolves it to a display name through the directory by-ids
   * lookup. Absent on older recordings (the field is optional on the wire).
   */
  readonly identity?: string | undefined;
}

/** One recording (recordingJSON on the wire: start/stop/detail responses and list items). */
export interface Recording {
  readonly id: number;
  readonly status: RecordingStatus;
  /** RFC 3339 timestamp of the recording start. */
  readonly startedAt: string;
  /** RFC 3339 timestamp once the recording reached a terminal state; null before. */
  readonly stoppedAt: string | null;
  readonly files: readonly RecordingFile[];
}

/** Failure of a recording endpoint: HTTP error, network failure or malformed response body. */
export class RecordingApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'RecordingApiError';
    this.status = status;
    this.code = code;
  }
}

/** True while the recording still runs (the indicator shows; the panel keeps polling). */
export function isActiveRecordingStatus(status: RecordingStatus): boolean {
  return isRecordingActive(status);
}

/**
 * The room's recording knowledge shared by the realtime hook (frames + reconciliation) and
 * the page: the state vocabulary plus the recording the state talks about.
 */
export interface RecordingSnapshot {
  readonly state: RecordingState;
  readonly recordingId: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseFileStatus(value: unknown): RecordingFileStatus | null {
  switch (value) {
    case 'pending':
    case 'ready':
    case 'missing':
    case 'processing':
    case 'no_key':
      return value;
    default:
      return null;
  }
}

function parseFilePurpose(value: unknown): RecordingFilePurpose | null {
  switch (value) {
    case 'audio':
    case 'video':
    case 'mp3':
    case 'transcript':
    case 'merged':
    case 'overview':
    case 'summary':
      return value;
    default:
      return null;
  }
}

/**
 * Narrows the optional signed playback URL (CR-29): a same-origin absolute path is accepted
 * verbatim; anything else (foreign origin, non-string) reads as absent - the row stays valid
 * and the player keeps its bearer fallback.
 */
function parseStreamUrl(value: unknown): string | undefined {
  if (typeof value === 'string' && value.startsWith('/')) {
    return value;
  }
  return undefined;
}

/** Sanity cap for a channel identity (the server shape is `kc-<uuid>[-screen]`, well under it). */
const IDENTITY_MAX_LENGTH = 256;

/**
 * Narrows the optional channel identity (CR-35): a non-empty string within the sane length cap
 * is accepted verbatim; anything else reads as absent (the naming falls back to «Channel N»).
 */
function parseIdentity(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0 && value.length <= IDENTITY_MAX_LENGTH) {
    return value;
  }
  return undefined;
}

function parseRecordingStatus(value: unknown): RecordingStatus | null {
  return value === 'starting' ||
    value === 'recording' ||
    value === 'stopping' ||
    value === 'completed' ||
    value === 'failed'
    ? value
    : null;
}

function parseTimestamp(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function parseNullableInt(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/**
 * Narrows one recording_files row. A row that violates the closed vocabulary (an unknown
 * purpose or status of a newer server) yields null: the row is skipped, never misinterpreted,
 * mirroring the frame parser's drop-don't-guess rule.
 */
export function parseRecordingFile(value: unknown): RecordingFile | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = parseNullableInt(value.id);
  const purpose = parseFilePurpose(value.purpose);
  const status = parseFileStatus(value.status);
  if (id === null || id < 0 || purpose === null || status === null) {
    return null;
  }
  const sizeBytes = value.sizeBytes === null ? null : parseNullableInt(value.sizeBytes);
  const durationMs = value.durationMs === null ? null : parseNullableInt(value.durationMs);
  return {
    id,
    purpose,
    status,
    sizeBytes,
    durationMs,
    streamUrl: parseStreamUrl(value.streamUrl),
    identity: parseIdentity(value.identity),
  };
}

/**
 * Narrows one recordings row. id/status/startedAt are required; files default to an empty list
 * when the key is missing (a recording mid-start has none yet).
 */
export function parseRecording(value: unknown): Recording | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = parseNullableInt(value.id);
  const status = parseRecordingStatus(value.status);
  const startedAt = parseTimestamp(value.startedAt);
  if (id === null || id < 0 || status === null || startedAt === null) {
    return null;
  }
  const files: RecordingFile[] = [];
  if (value.files !== undefined) {
    if (!Array.isArray(value.files)) {
      return null;
    }
    for (const entry of value.files) {
      const file = parseRecordingFile(entry);
      if (file === null) {
        return null;
      }
      files.push(file);
    }
  }
  let stoppedAt: string | null = null;
  if (value.stoppedAt !== undefined && value.stoppedAt !== null) {
    stoppedAt = parseTimestamp(value.stoppedAt);
    if (stoppedAt === null) {
      return null;
    }
  }
  return { id, status, startedAt, stoppedAt, files };
}

/**
 * Narrows a list response body ({recordings: [...]}) to the recordings. The top level is
 * strict (a malformed body is an invalid_response error); individual rows that violate the
 * vocabulary are skipped so one alien row never blanks the whole history.
 */
export function parseRecordingsList(body: unknown): readonly Recording[] {
  if (!isRecord(body) || !Array.isArray(body.recordings)) {
    throw new TypeError('recordings response must carry a "recordings" array');
  }
  const recordings: Recording[] = [];
  for (const entry of body.recordings) {
    const recording = parseRecording(entry);
    if (recording !== null) {
      recordings.push(recording);
    }
  }
  return recordings;
}

/** Builds the RecordingApiError for a non-2xx response from the contract error envelope. */
async function errorFromResponse(response: Response, endpoint: string): Promise<RecordingApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // non-JSON error body, keep the generic message
  }
  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new RecordingApiError(response.status, code, message);
}

async function recordingRequest(
  room: string,
  path: 'recording/start' | 'recording/stop',
  token: string,
): Promise<Recording> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/rooms/${encodeURIComponent(room)}/${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new RecordingApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, path);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new RecordingApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  if (!isRecord(body)) {
    throw new RecordingApiError(response.status, 'invalid_response', 'response must be an object');
  }
  const recording = parseRecording(body.recording);
  if (recording === null) {
    throw new RecordingApiError(
      response.status,
      'invalid_response',
      'response recording violates the recording shape',
    );
  }
  return recording;
}

/** POST /api/v1/rooms/{room}/recording/start (moderator/admin). The WS frame follows the 200. */
export function startRecording(room: string, token: string): Promise<Recording> {
  return recordingRequest(room, 'recording/start', token);
}

/** POST /api/v1/rooms/{room}/recording/stop (moderator/admin or the recording starter). */
export function stopRecording(room: string, token: string): Promise<Recording> {
  return recordingRequest(room, 'recording/stop', token);
}

/** GET /api/v1/rooms/{room}/recordings (moderator/admin), newest first per the server. */
export async function listRecordings(room: string, token: string): Promise<readonly Recording[]> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/rooms/${encodeURIComponent(room)}/recordings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new RecordingApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'recordings list');
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new RecordingApiError(
      response.status,
      'invalid_response',
      'recordings response is not valid JSON',
    );
  }
  try {
    return parseRecordingsList(body);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed recordings response';
    throw new RecordingApiError(response.status, 'invalid_response', detail);
  }
}

/** File extension per purpose; the transcript downloads as the canonical .json artifact. */
export function purposeExtension(purpose: RecordingFilePurpose): string {
  switch (purpose) {
    case 'audio':
      return 'ogg';
    case 'video':
    case 'merged':
    case 'overview':
      return 'mp4';
    case 'mp3':
      return 'mp3';
    case 'transcript':
      return 'json';
    case 'summary':
      return 'txt';
  }
}

/** Download filename per the M6 UI convention: `<room>-<recid>-<purpose>.<ext>`. */
export function recordingFileName(
  room: string,
  recordingId: number,
  purpose: RecordingFilePurpose,
): string {
  return `${room}-${String(recordingId)}-${purpose}.${purposeExtension(purpose)}`;
}

/**
 * Download filename for the archive page convention: `<room>-<purpose>.<ext>` (the room
 * context already identifies the recording there, so the id is omitted).
 */
export function recordingFileNameByPurpose(room: string, purpose: RecordingFilePurpose): string {
  return `${room}-${purpose}.${purposeExtension(purpose)}`;
}

/**
 * GET /api/v1/rooms/{room}/recordings/{id}/files/{fileId}/download, returned as a Blob for the
 * object-URL download flow. The join JWT or SSO bearer travels in the Authorization header, so
 * the browser's plain-link download cannot be used (it would drop the header).
 */
export async function fetchRecordingFile(
  room: string,
  recordingId: number,
  fileId: number,
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<Blob> {
  let response: Response;
  try {
    response = await fetchImpl(
      `/api/v1/rooms/${encodeURIComponent(room)}/recordings/${String(recordingId)}/files/${String(fileId)}/download`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    throw new RecordingApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'recording download');
  }
  return response.blob();
}

/** One progress sample of a streamed artifact download (bytes on the wire so far). */
export interface FetchProgress {
  readonly loaded: number;
  /** Total bytes when the response carried Content-Length (ServeContent always does). */
  readonly total: number | null;
}

/**
 * The download route fetched as a STREAM: the caller watches bytes arrive (the player shows a
 * Content-Length-based progress bar) and the assembled Blob lands only once - the same bytes
 * the plain fetchRecordingFile returns. abort kills the transfer mid-flight (switching files in
 * the player); an aborted fetch rejects with the fetch signal's AbortError, not RecordingApiError.
 */
export async function fetchRecordingFileProgress(
  room: string,
  recordingId: number,
  fileId: number,
  token: string,
  onProgress: (progress: FetchProgress) => void,
  fetchImpl: typeof fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<Blob> {
  let response: Response;
  const init: RequestInit = { headers: { Authorization: `Bearer ${token}` } };
  if (signal !== undefined) {
    init.signal = signal;
  }
  try {
    response = await fetchImpl(
      `/api/v1/rooms/${encodeURIComponent(room)}/recordings/${String(recordingId)}/files/${String(fileId)}/download`,
      init,
    );
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    throw new RecordingApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'recording download');
  }
  const lengthHeader = response.headers.get('Content-Length');
  const total =
    lengthHeader !== null && /^\d+$/.test(lengthHeader) ? Number(lengthHeader) : null;
  const body = response.body;
  if (body === null) {
    // No streaming body (test doubles, exotic runtimes): fall back to one buffered blob.
    const blob = await response.blob();
    onProgress({ loaded: blob.size, total: total ?? blob.size });
    return blob;
  }
  const chunks: BlobPart[] = [];
  const reader = body.getReader();
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (value !== undefined) {
      chunks.push(new Uint8Array(value));
      loaded += value.byteLength;
      onProgress({ loaded, total });
    }
  }
  return new Blob(chunks);
}

/**
 * POST /api/v1/rooms/{room}/recordings/{id}/summary/retry (moderator/admin, CR-30): re-enqueues
 * the recording's summary job after a failure - the «Повторить» button behind the Саммари
 * tab's error state. The server answers 200 with the recording whether it enqueued a fresh job
 * or one was already in flight.
 */
export async function retrySummary(
  room: string,
  recordingId: number,
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<Recording> {
  let response: Response;
  try {
    response = await fetchImpl(
      `/api/v1/rooms/${encodeURIComponent(room)}/recordings/${String(recordingId)}/summary/retry`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    throw new RecordingApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'summary retry');
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new RecordingApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  if (!isRecord(body)) {
    throw new RecordingApiError(response.status, 'invalid_response', 'response must be an object');
  }
  const recording = parseRecording(body.recording);
  if (recording === null) {
    throw new RecordingApiError(
      response.status,
      'invalid_response',
      'response recording violates the recording shape',
    );
  }
  return recording;
}

/**
 * Stable kinds for the recording toasts (recording.error_* in the i18n catalog). The
 * no-media case is the 400 invalid_request answer of the start route; unknown codes and
 * non-API errors map to the generic kind.
 */
export type RecordingErrorKind =
  'forbidden' | 'active' | 'inactive' | 'disabled' | 'no_media' | 'storage' | 'other';

export function recordingErrorKind(err: unknown): RecordingErrorKind {
  if (!(err instanceof RecordingApiError)) {
    return 'other';
  }
  switch (err.code) {
    case 'forbidden':
      return 'forbidden';
    case 'recording_active':
      return 'active';
    case 'recording_inactive':
      return 'inactive';
    case 'recording_disabled':
      return 'disabled';
    case 'invalid_request':
      return err.status === 400 ? 'no_media' : 'other';
    case 'storage_error':
    case 'upstream_error':
      return 'storage';
    default:
      return 'other';
  }
}
